/**
 * BLRRD Pointwise Filters — Gradient Map
 *
 * Map per-pixel luminance Y∈[0,1] through a user-defined gradient
 * (list of [t, color] stops) to produce a stylized recoloring.
 * Common look — replicate Lightroom split-toning, duotone newspaper
 * print, or InfraRed false-color.
 *
 * Algorithm: standard photo-editing math (Photoshop "Gradient Map"
 * since v5, 1998 — public). For each pixel:
 *
 *   t = luminance(R, G, B)  ∈ [0, 1]
 *   out = evalGradient(stops, t)  ∈ Float32 RGB
 *
 * Pipeline slot: KERNEL-STAGE scene-linear ACEScg. Gradient stops
 * are interpreted in the same space as the buffer (scene-linear)
 * so the user gets a predictable result through filmic.
 *
 * Identity short-circuit: stops = [{t:0, color:[0,0,0]}, {t:1,
 * color:[1,1,1]}] (linear black-to-white) → no-op (Y maps to itself).
 *
 * NOTE on dependency on Wave 12.1 (`src/tools/gradient.js`):
 * Wave 12.1 ships `evalGradient`. To keep this module standalone
 * (Wave 11.2 lands before/parallel to 12.1) we inline a minimal
 * evalGradient here. At integration time, Lead can swap to the
 * shared util by changing the import — same API shape.
 */

const IDENTITY_EPS = 1e-6;

/** Default Gradient Map params — black-to-white linear ramp. */
export function defaultGradientMapParams() {
    return {
        gradient: {
            stops: [
                { t: 0, color: [0, 0, 0] },
                { t: 1, color: [1, 1, 1] },
            ],
        },
        channel: 'luminance',
        strength: 1.0,
    };
}

/** True iff `params.gradient` is the identity (linear black→white). */
export function isIdentityGradientMap(params) {
    if (!params) return true;
    if (params.strength != null && params.strength <= 0) return true;
    const g = params.gradient;
    if (!g || !Array.isArray(g.stops) || g.stops.length < 2) return true;
    /* Identity if every stop is on the diagonal AND strength is 1.
     * Slightly loose: caller could declare 3 stops on the diagonal
     * — still identity. */
    for (const stop of g.stops) {
        const t = stop.t;
        const c = stop.color;
        if (!c) return false;
        if (Math.abs(c[0] - t) > IDENTITY_EPS) return false;
        if (Math.abs(c[1] - t) > IDENTITY_EPS) return false;
        if (Math.abs(c[2] - t) > IDENTITY_EPS) return false;
    }
    return true;
}

/**
 * Apply Gradient Map to a Float32 RGBA buffer in place.
 *
 * @param {Float32Array} rgba
 * @param {number}       w
 * @param {number}       h
 * @param {object}       params      { gradient: {stops}, channel, strength? }
 * @param {object}       [opts]      { selectionMask? }
 * @returns {Float32Array}           same `rgba` reference
 */
export function applyGradientMap(rgba, w, h, params, opts) {
    validateBuffer(rgba, w, h);
    if (isIdentityGradientMap(params)) return rgba;
    const mask = opts && opts.selectionMask ? opts.selectionMask : null;
    if (mask && mask.length !== w * h) {
        throw new Error('applyGradientMap: selectionMask length mismatch');
    }

    /* Pre-sort stops once. */
    const stops = sortStops(params.gradient.stops);
    const strength = params.strength == null ? 1 : params.strength;
    const channel  = params.channel || 'luminance';
    const N = w * h;

    for (let p = 0; p < N; p++) {
        const i = p << 2;
        const m = mask ? mask[p] : 1;
        if (m <= 0) continue;
        const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
        let t;
        if (channel === 'luminance') {
            t = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        } else if (channel === 'r') {
            t = r;
        } else if (channel === 'g') {
            t = g;
        } else if (channel === 'b') {
            t = b;
        } else if (channel === 'max') {
            t = r > g ? (r > b ? r : b) : (g > b ? g : b);
        } else {
            t = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        }
        if (t < 0) t = 0; else if (t > 1) t = 1;
        const c = evalGradient(stops, t);
        const newR = (1 - strength) * r + strength * c[0];
        const newG = (1 - strength) * g + strength * c[1];
        const newB = (1 - strength) * b + strength * c[2];
        if (m >= 1) {
            rgba[i]     = newR;
            rgba[i + 1] = newG;
            rgba[i + 2] = newB;
        } else {
            const inv = 1 - m;
            rgba[i]     = inv * r + m * newR;
            rgba[i + 1] = inv * g + m * newG;
            rgba[i + 2] = inv * b + m * newB;
        }
    }
    return rgba;
}

/**
 * Evaluate a sorted list of gradient stops at t∈[0,1].
 * Linear RGB interpolation between adjacent stops.
 *
 * @param {Array<{t:number, color:[number,number,number]}>} stops  (sorted)
 * @param {number} t
 * @returns {[number, number, number]}
 */
export function evalGradient(stops, t) {
    const n = stops.length;
    if (n === 0) return [0, 0, 0];
    if (t <= stops[0].t)         return stops[0].color.slice();
    if (t >= stops[n - 1].t)     return stops[n - 1].color.slice();
    /* Bisect for the segment containing t. */
    let lo = 0, hi = n - 1;
    while (lo + 1 < hi) {
        const mid = (lo + hi) >> 1;
        if (stops[mid].t <= t) lo = mid;
        else hi = mid;
    }
    const a = stops[lo], b = stops[hi];
    const denom = b.t - a.t;
    const f = denom > 1e-9 ? (t - a.t) / denom : 0;
    const oneF = 1 - f;
    return [
        oneF * a.color[0] + f * b.color[0],
        oneF * a.color[1] + f * b.color[1],
        oneF * a.color[2] + f * b.color[2],
    ];
}

function sortStops(stops) {
    const out = stops.map(s => ({ t: s.t, color: [s.color[0], s.color[1], s.color[2]] }));
    out.sort((x, y) => x.t - y.t);
    return out;
}

function validateBuffer(rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new TypeError('applyGradientMap: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('applyGradientMap: rgba.length !== w*h*4');
    }
}
